import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { BoostStatusBadge } from "@/components/ads/boost-status-badge";
import { useLocale, formatNumber, formatDate } from "@/i18n/format";
import { useBoost } from "@/hooks/use-ads";

interface BoostDetailSheetProps {
  brandId: string;
  boostId: string | null;
  onClose: () => void;
}

function num(v: string | number | null | undefined): number {
  const n = typeof v === "string" ? Number(v) : (v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

export function BoostDetailSheet({ brandId, boostId, onClose }: BoostDetailSheetProps) {
  const { t } = useTranslation("ads");
  const locale = useLocale();
  const open = boostId !== null;
  const { data, isLoading, isError } = useBoost(brandId, boostId ?? "");

  const daily = data?.daily ?? [];
  const chartData = daily.map((d) => ({
    date: d.date,
    spend: num(d.spendUsd),
    impressions: num(d.impressions),
  }));

  function usdFmt(n: number): string {
    return formatNumber(n, locale, { style: "currency", currency: "USD" });
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[540px] sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("boostDetail.title")}</SheetTitle>
        </SheetHeader>

        {isError ? (
          <div className="mt-6 flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-muted-foreground">{t("boostDetail.notFound")}</p>
            <Button variant="outline" size="sm" onClick={onClose}>
              {t("boostDetail.closeButton")}
            </Button>
          </div>
        ) : isLoading || !data ? (
          <div className="mt-4 h-64 animate-pulse rounded-lg bg-muted" />
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-start gap-3">
              {data.piece?.thumbnailUrl ? (
                <img
                  src={data.piece.thumbnailUrl}
                  alt=""
                  className="h-14 w-14 flex-shrink-0 rounded object-cover"
                />
              ) : (
                <div className="h-14 w-14 flex-shrink-0 rounded bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {data.piece && <PlatformIcon platform={data.piece.platform} size={16} />}
                  <span className="text-sm font-medium">{data.piece?.shortCode ?? data.pieceId}</span>
                  <BoostStatusBadge status={data.status} />
                </div>
                {data.piece?.caption && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {data.piece.caption}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {data.goal} · {data.mode} {t("boostDetail.mode")}
                  {data.startDate &&
                    ` · ${formatDate(data.startDate, locale)}–${
                      data.endDate ? formatDate(data.endDate, locale) : "…"
                    }`}
                </p>
              </div>
            </div>

            {data.error && (
              <p className="rounded-md bg-red-500/10 p-2 text-xs text-red-400">{data.error}</p>
            )}

            <div className="grid grid-cols-5 gap-2">
              <Total label={t("boostDetail.total.spend")} value={usdFmt(num(data.spendToDateUsd))} />
              <Total label={t("boostDetail.total.impr")} value={formatNumber(num(data.impressions), locale)} />
              <Total label={t("boostDetail.total.clicks")} value={formatNumber(num(data.clicks), locale)} />
              <Total label={t("boostDetail.total.cpm")} value={data.cpm !== null ? usdFmt(data.cpm) : "—"} />
              <Total label={t("boostDetail.total.cpc")} value={data.cpc !== null ? usdFmt(data.cpc) : "—"} />
            </div>

            {chartData.length === 0 ? (
              <div className="flex h-40 items-center justify-center rounded-lg border bg-muted/20 text-sm text-muted-foreground">
                {t("boostDetail.chart.noData")}
              </div>
            ) : (
              <>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="spend"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="impressions"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-1 pr-2 font-medium">{t("boostDetail.table.date")}</th>
                      <th className="py-1 pr-2 font-medium">{t("boostDetail.table.spend")}</th>
                      <th className="py-1 pr-2 font-medium">{t("boostDetail.table.impr")}</th>
                      <th className="py-1 pr-2 font-medium">{t("boostDetail.table.reach")}</th>
                      <th className="py-1 pr-2 font-medium">{t("boostDetail.table.clicks")}</th>
                      <th className="py-1 pr-2 font-medium">{t("boostDetail.table.ctr")}</th>
                      <th className="py-1 font-medium">{t("boostDetail.table.eng")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daily.map((d) => (
                      <tr key={d.date} className="border-b border-border/50">
                        <td className="py-1 pr-2">{d.date}</td>
                        <td className="py-1 pr-2">{usdFmt(num(d.spendUsd))}</td>
                        <td className="py-1 pr-2">{formatNumber(num(d.impressions), locale)}</td>
                        <td className="py-1 pr-2">{formatNumber(num(d.reach), locale)}</td>
                        <td className="py-1 pr-2">{formatNumber(num(d.clicks), locale)}</td>
                        <td className="py-1 pr-2">
                          {d.ctr !== null ? `${(d.ctr * 100).toFixed(2)}%` : "—"}
                        </td>
                        <td className="py-1">{formatNumber(num(d.engagement), locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
